import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { auth, functions, db } from '../utils/firebase';
import { clearUserCache } from '../utils/userDataCache';
import { clearAuthCache } from '../utils/optimizedAuth';
// import { ramCache } from '../core/ramCache';
import { getEntitlementsSummary } from '../utils/entitlementsApi';

// Removed unused ENABLE_HTTP_FALLBACK variable

const BillingSuccess = () => {
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // HTTP fallback function for verification
  async function verifySessionHttp(sessionId) {
    const token = await auth.currentUser.getIdToken(false);
    const resp = await fetch("https://us-central1-trivia-plug-3.cloudfunctions.net/verifyCheckoutSessionHttp", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ sessionId }),
    });
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // Polling function to wait for purchase to be processed
  async function waitForPurchase({ uid, gameId, maxMs = 60000 }) {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      const libRef = doc(db, `users/${uid}/library/${gameId}`);
      const snap = await getDoc(libRef);
      if (snap.exists()) return true;
      await new Promise(r => setTimeout(r, 1500)); // 1.5s backoff
    }
    return false;
  }

  // BULLETPROOF AUTOMATIC REDIRECT: This runs immediately when page loads
  useEffect(() => {
    // Check if we're on Stripe's checkout page (this handles the case where Stripe redirect fails)
    if (window.location.hostname === 'checkout.stripe.com') {
      console.log('🚨 AUTOMATIC DETECTION: User stuck on Stripe page, forcing immediate redirect...');
      
      // Extract session ID from Stripe URL
      const stripeUrl = window.location.href;
      const sessionIdMatch = stripeUrl.match(/cs_test_[a-zA-Z0-9]+/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[0];
        console.log('🔄 AUTOMATIC REDIRECT: Extracted session ID:', sessionId);
        
        // IMMEDIATE redirect to our success page - no user action required
        const successUrl = `${window.location.protocol}//${window.location.hostname === 'localhost' ? 'localhost:3000' : 'trivia-plug-3.web.app'}/billing/success?session_id=${sessionId}&force_redirect=true`;
        console.log('🔄 AUTOMATIC REDIRECT TO:', successUrl);
        
        // Use window.location.replace for immediate redirect - no back button
        window.location.replace(successUrl);
        return; // Stop execution here
      } else {
        console.log('❌ Could not extract session ID, redirecting to home');
        window.location.replace('http://localhost:3000');
        return; // Stop execution here
      }
    }
  }, []);

  useEffect(() => {
    const verifySession = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get('session_id');
        // const successFlag = urlParams.get('success'); // Unused - removed
        const forceRedirect = urlParams.get('force_redirect');
        
        if (!sessionId) {
          setError('No session ID found');
          setStatus('error');
          return;
        }

        if (forceRedirect) {
          console.log('🔄 FORCE REDIRECT: Processing automatic redirect from Stripe page');
        }

        setStatus('verifying');
        
        // 1) Server-side verify (no client reads of protected docs)
        console.log('🔐 BillingSuccess: Checking authentication...');
        console.log('🔐 BillingSuccess: auth.currentUser:', auth.currentUser);
        console.log('🔐 BillingSuccess: auth.currentUser?.uid:', auth.currentUser?.uid);
        
        // Ensure user is authenticated
        if (!auth.currentUser) {
          console.error('🔐 BillingSuccess: No authenticated user found');
          setError('Authentication required. Please log in again.');
          setStatus('error');
          return;
        }
        
        // Force refresh the token to ensure it's current
        console.log('🔐 BillingSuccess: Refreshing auth token...');
        await auth.currentUser.getIdToken(false);
        
        // Test callable authentication first
        console.log('🔐 BillingSuccess: Testing callable authentication...');
        try {
          const diag = httpsCallable(functions, 'diagnoseCallableAuth');
          const { data: diagData } = await diag({});
          console.info('🔐 BillingSuccess: DIAG:', diagData, 'clientUID:', auth.currentUser?.uid);
        } catch (diagError) {
          console.error('🔐 BillingSuccess: Diagnostic call failed:', diagError);
        }
        
        // TEMPORARILY FORCE HTTP PATH to unblock purchases while fixing auth
        console.log('🔐 BillingSuccess: Using HTTP fallback to avoid 401 loop');
        const data = await verifySessionHttp(sessionId);
        console.info("VERIFY RESPONSE ▶", data);
        
        const { uid, gameId, chargeType } = data;

        setResult(data);
        setStatus('refreshing');

        // 2) Bust all relevant caches and force-refresh Firebase token
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(false); // Force refresh custom claims
        }
        
        // Clear all caches
        clearUserCache(auth.currentUser?.uid);
        clearAuthCache(auth.currentUser?.uid);
        
        // Clear localStorage manually
        const keysToRemove = [
          `authClaimsCache_${auth.currentUser?.uid}`,
          `authTokenCache_${auth.currentUser?.uid}`,
          `userDataCache_${auth.currentUser?.uid}`,
          `dashboardStatsCache_${auth.currentUser?.uid}`
        ];

        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
        });

        setStatus('success');

        // 3) Refresh RAM cache with new entitlements
        try {
          console.log('🔄 BillingSuccess: Refreshing RAM cache with new entitlements...');
          
          // Force refresh Firebase auth token to get latest claims
          if (auth.currentUser) {
            await auth.currentUser.getIdToken(false);
            await auth.currentUser.reload();
          }
          
          // const entitlements = await getEntitlementsSummary(); // Unused - removed
          // ramCache.upsertEntitlements?.(entitlements);
          console.log('✅ BillingSuccess: RAM cache refreshed successfully');
        } catch (error) {
          console.error('❌ BillingSuccess: Failed to refresh RAM cache:', error);
        }

        // 4) For marketplace purchases, poll the ONLY allowed path the client needs
        if (gameId && uid) {
          console.log('⏳ Waiting for purchase to be processed...');
          setStatus('processing');
          
          const ok = await waitForPurchase({ uid, gameId });
          
          if (!ok) {
            console.warn('⚠️ Purchase processing timeout - proceeding anyway');
            setError("Purchase succeeded, finalizing… This may take a moment. If this persists, contact support.");
          }
        }

        // 4) Redirect to appropriate dashboard
        let targetUrl;
        if (chargeType === 'subscription') {
          targetUrl = '/home';
        } else if (chargeType === 'created_game_launch') {
          // $15 created game launch - get session ID from user's active sessions
          try {
            const userActiveSessionRef = doc(db, `users/${uid}/activeSessions/${gameId}`);
            const userActiveSessionSnap = await getDoc(userActiveSessionRef);
            
            if (userActiveSessionSnap.exists()) {
              const sessionData = userActiveSessionSnap.data();
              const sessionId = sessionData.sessionId;
              console.log('🎯 Found session ID for redirect:', sessionId);
              targetUrl = `/launch-game/${gameId}?session=${encodeURIComponent(sessionId)}`;
            } else {
              console.warn('⚠️ No active session found, redirecting without session ID');
              targetUrl = `/launch-game/${gameId}`;
            }
          } catch (error) {
            console.error('❌ Error getting session ID:', error);
            targetUrl = `/launch-game/${gameId}`;
          }
        } else if (gameId) {
          // Marketplace purchase - redirect to My Games to show the purchased game
          targetUrl = '/my-games?tab=purchased';
        } else {
          targetUrl = '/home';
        }
        console.log('🔄 AUTOMATIC REDIRECT TO:', targetUrl);
        
        // Use window.location.replace for immediate redirect - no back button
        window.location.replace(targetUrl);

      } catch (error) {
        console.error('Error verifying checkout session:', error);
        
        // Set user-friendly error message
        let errorMessage = 'We couldn\'t verify your payment right now.';
        if (error.code === 'unavailable' || error.message?.includes('network') || error.message?.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to our server. Your payment was successful, but we couldn\'t confirm it. Please use "Refresh Access" or "Force Redirect" below.';
        } else if (error.code === 'permission-denied' || error.message?.includes('permission')) {
          errorMessage = 'Unable to verify payment due to permission restrictions. Please try refreshing your access or contact support.';
        } else if (error.message?.includes('Session does not belong')) {
          errorMessage = 'Payment verification failed. Your payment was processed, but we couldn\'t link it to your account. Please try "Refresh Access" or contact support.';
        } else {
          errorMessage = 'Your payment was successful, but we encountered an issue verifying it. Please use "Refresh Access" to reload your account, or contact support if this continues.';
        }
        
        setError(errorMessage);
        setStatus('error');
      }
    };

    verifySession();
  }, [location, navigate, auth]);

  const handleManualRefresh = async () => {
    try {
      setStatus('refreshing');
      
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(false);
      }
      
      clearUserCache(auth.currentUser?.uid);
      clearAuthCache(auth.currentUser?.uid);
      
      // Clear localStorage
      const keysToRemove = [
        `authClaimsCache_${auth.currentUser?.uid}`,
        `authTokenCache_${auth.currentUser?.uid}`,
        `userDataCache_${auth.currentUser?.uid}`,
        `dashboardStatsCache_${auth.currentUser?.uid}`
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      setStatus('success');
      
      // Immediate redirect
      let targetUrl;
      if (result?.mode === 'subscription') {
        targetUrl = '/creator-dashboard';
      } else if (result?.gameId) {
        // Marketplace purchase - redirect to My Games to show the purchased game
        targetUrl = '/my-games?tab=purchased';
      } else {
        targetUrl = '/home';
      }
      window.location.replace(targetUrl);

    } catch (error) {
      console.error('Error in manual refresh:', error);
      
      // Set user-friendly error message
      let errorMessage = 'Unable to refresh your access right now.';
      if (error.code === 'unavailable' || error.message?.includes('network') || error.message?.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to our server. Please check your internet connection and try again, or use "Force Redirect" to continue.';
      } else {
        errorMessage = 'Refresh failed. Please try "Force Redirect" to go to your dashboard, or contact support if this continues.';
      }
      
      setError(errorMessage);
      setStatus('error');
    }
  };

  const handleGoHome = () => {
    window.location.replace('/home');
  };

  const handleForceRedirect = () => {
    // Force a hard redirect
    const targetUrl = '/home';
    window.location.replace(targetUrl);
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Payment</h2>
            <p className="text-gray-600">Please wait while we confirm your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'refreshing') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Confirmed!</h2>
            <p className="text-gray-600">Updating your account access...</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">
              {result?.mode === 'subscription' 
                ? `Welcome to Creator ${result.role === 'creator_lite' ? 'Lite' : 'Pro'}!`
                : 'Your game launch has been confirmed!'
              }
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Redirecting you to your dashboard...
            </p>
            <div className="space-y-3">
              <button
                onClick={handleForceRedirect}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard Now
              </button>
              <button
                onClick={handleGoHome}
                className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="space-y-3">
              <button
                onClick={handleManualRefresh}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Refresh Access
              </button>
              <button
                onClick={handleForceRedirect}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
              >
                Force Redirect to Dashboard
              </button>
              <button
                onClick={handleGoHome}
                className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BillingSuccess;
